// The "who has tasted what" table of the switch sheet, built from the session's own view: one
// column per person still coming (those who said no are not waited for), one row per wine. The
// cell is a check when that person's note is in and "now" for the wine the viewer has in front of
// them. Where someone else is, and in an open tasting what they picked, is the live strip's
// business (components/tasting/CoTasterStrip.tsx), never this table's.
import type { TastingSessionView } from "../social/tastingSession";

export type Cell = "done" | "now" | "todo";

export interface MatrixPerson {
  readonly userId: string;
  readonly name: string;
  /** Asked, but not yet in the tasting. */
  readonly invited: boolean;
}

export interface MatrixRow {
  readonly cells: readonly Cell[];
  /** How many people have this wine done. */
  readonly done: number;
}

export interface ProgressMatrix {
  readonly people: readonly MatrixPerson[];
  readonly rows: readonly MatrixRow[];
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

export function progressMatrix(input: {
  session: TastingSessionView | undefined;
  viewerId: string | undefined;
  /** The index of the wine the viewer is on. */
  current: number;
}): ProgressMatrix {
  const { session, viewerId, current } = input;
  if (session === undefined || session.flight === null) return { people: [], rows: [] };
  const people = session.participants
    .filter((p) => p.rsvp !== "no")
    .map((p): MatrixPerson => ({
      userId: p.userId,
      name: p.profile.displayName,
      invited: p.status === "invited",
    }));
  const rows = session.flight.wines
    .toSorted((a, b) => a.index - b.index)
    .map((wine): MatrixRow => {
      const cells = people.map((p): Cell => {
        if (wine.submittedBy.includes(p.userId)) return "done";
        return p.userId === viewerId && wine.index === current ? "now" : "todo";
      });
      return { cells, done: cells.filter((c) => c === "done").length };
    });
  return { people, rows };
}
